import { useTranslate } from "react-admin";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
} from "@mui/x-data-grid";

import {
  downloadTransactionDetails,
  getOperation,
  getOperationAll,
  getOperationDetails,
  getOperationDetailStat,
  getOpManualReason,
  reEstimateFee,
} from "../../../api/merchant";
import {
  OpApprove,
  OpDownload,
  OpEstimate,
  OpRedirect,
  OpSign,
  OpView,
} from "../../../assets/icons";
import { CustomPagination } from "../../../components/CustomPagination";
import DialogInOne from "../../../components/DialogInOne";
import { ToolCryptoText } from "../../../components/MuiGenerals";
import { NoRowsOverlay } from "../../../components/NoRowsOverlay";
import OpIconButton from "../../../components/OpIconButton";
import { useAlerting, usePermission } from "../../../hooks";
import useColumnHide, {
  ColumnHiddenMessage,
  getColumnHideConfig,
} from "../../../hooks/useColumnHide";
import {
  dataGridDefaults,
  enumNewMerchantOperationStatus,
  enumOperationType,
  FeatureCodes,
  tableConfig,
} from "../../../utils/constant";
import {
  calculateSum,
  displayAmount,
  enumMapping,
  getDecimal,
  isOperationSmartAccount,
  todaysDateForFileName,
  toDisplayTime,
} from "../../../utils/helper";
import { getFilteredColumns, useGenGridCol } from "../../../utils/HelperComp";
import { useZusDialogStore } from "../../../zustand/store";
import { opDayStat } from "./types";
import MultisigCell from "./components/MultisigCell";
import { NON_SWAP_TAB_OMIT_KEYS, SWAP_TAB_OMIT_KEYS } from "./constants";
import BigNumber from "bignumber.js";
import { SwapExecutionType } from "@wallet-manager/node-types/dist/src/postgres/const";
import { operationCreatorFilterEnum } from "./MerchantOperationFilterBar";
import { IoperationTab } from "./types/IoperationTab";
const Pkey = FeatureCodes.assetManagement.MerchantOperation;
const translatePrefix = "assetManagement";
export const Enum = {
  newMerchantOperationStatus: enumMapping(enumNewMerchantOperationStatus),
  operationType: enumMapping(enumOperationType),
};

const getOneFromAll =
  (apiFn: typeof getOperationAll) =>
  async (operation_creator_filter: number, batchId: string) => {
    const res = await apiFn({
      batchIdArr: [batchId],
      operation_creator_filter,
      pageSize: 20,
      page: 0,
    });
    return res.rows[0] || {};
  };

export const getTabApis = (tab: IoperationTab) => {
  return {
    download: downloadTransactionDetails(tab),
    opDetail: getOperationDetails(tab),
    stat: getOperationDetailStat(tab),
    opReason: getOpManualReason(tab),
    estimate: reEstimateFee(tab),
    getOne: getOneFromAll(getOperation(tab)),
  };
};

export default function MerchantOperationList(props: any) {
  const translate = useTranslate();
  const { hasPermission } = usePermission();
  const t = (key: string) => translate(`${translatePrefix}.${key}`);
  const { list, setPage, page, setUploadDialog, listMapping } = props;
  const tabName = props.tabName as IoperationTab;
  const totalRecords = list.count;
  const content: GridRowsProp = listMapping("key", list.rows, {
    translate,
    operationTab: tabName,
  });
  const cellButtons = (
    params: GridRenderCellParams,
    canShow: {
      viewDetail: boolean;
      approval: boolean;
      manualVerification: boolean;
      download: boolean;
      sign: boolean;
    },
  ) => {
    return (
      <ActionComponent {...{ params, setUploadDialog, tabName, canShow }} />
    );
  };
  const tabApi = getTabApis(tabName);
  const zusDialog = useZusDialogStore();
  const HistoryButton = (item: GridRenderCellParams) => {
    const onClick = async () => {
      const operationType = String(item.row.rawData.operation_type);
      const { batch_id } = item.row;
      const reason: any =
        operationType === enumOperationType.Withdraw
          ? await tabApi.opReason({ batch_id })
          : "";
      const time =
        operationType === enumOperationType.Withdraw
          ? await tabApi.stat({ batch_id })
          : "";

      const checking_hour = time ? time.checking_hour : "";
      const approveReason = reason
        ? reason.map((item: any) => ({
            ...item,
            checking_hour: checking_hour,
          }))
        : "";

      const combineContent = {
        ...item.row,
        approveReason,
        isHistory: true,
        opArrDetail: [],
        uniswapDetailsArr: [],
      };
      zusDialog.open("approvalDialog", {
        from: "progress",
        row: combineContent,
      });
    };

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "55%",
        }}
      >
        <span>{item.row?.approval_progress}</span>
        <OpIconButton
          title={""}
          url={OpRedirect}
          onClick={onClick}
          size="1rem"
        />
      </Box>
    );
  };
  const ListEstimatedCell = (item: Partial<GridRenderCellParams>) => {
    const batch_id = item.row.batch_id;
    const onClick = async () => {
      const operation_creator_filter = operationCreatorFilterEnum[tabName];
      const getOne = getTabApis(tabName).getOne;
      const res = await getOne(operation_creator_filter, batch_id);
      const { miner_fee_asset_name } = res;
      const latest_estimated_miner_fee = displayAmount(
        res.estimated_miner_fee,
        res.estimated_miner_fee_decimals,
      );

      const display_estimated_miner_fee = res.is_estimating_miner_fee
        ? t("estimating")
        : latest_estimated_miner_fee;
      zusDialog.open("operationDialog", {
        batch_id,
        status: res.status,
        miner_fee_asset_name,
        estimated_miner_fee: display_estimated_miner_fee,
        last_estimated_miner_fee_date: toDisplayTime(
          res.last_estimated_miner_fee_date,
        ),
      });
    };

    const old_estimated_miner_fee = item.row.estimated_miner_fee;
    const display_estimated_miner_fee = (function () {
      if (item.row.rawData?.is_estimating_miner_fee) return t("estimating");
      return old_estimated_miner_fee || "";
    })();
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          width: "100%",
          columnGap: ".5rem",
        }}
      >
        <ToolCryptoText
          tooltip={display_estimated_miner_fee}
          width={"max-content"}
        />
        {!item.row?.rawData?.is_multisign &&
          !isOperationSmartAccount(item.row.rawData.smart_account_type) && (
            <OpIconButton
              title={""}
              url={OpRedirect}
              onClick={onClick}
              isDisabled={item.row.rawData?.is_estimating_miner_fee}
              size="1rem"
            />
          )}
      </Box>
    );
  };

  const isTab = {
    all: tabName === "all",
    api: tabName === "api_withdraw_tab",
    portal: tabName === "portal_withdraw_tab",
    sweep: tabName === "sweep_tab",
    swap: tabName === "swap_tab",
    contract_sweep: tabName === "contract_sweep", //?
  };

  const canShow = {
    viewDetail:
      (isTab.all && hasPermission(Pkey.TabAll.ViewDetail)) ||
      (isTab.api && hasPermission(Pkey.TabApiWithdraw.ViewDetail)) ||
      (isTab.portal && hasPermission(Pkey.TabPortalWithdraw.ViewDetail)) ||
      (isTab.sweep && hasPermission(Pkey.TabSweep.ViewDetail)) ||
      (isTab.swap && hasPermission(Pkey.TabSwap.ViewDetail)) ||
      (isTab.contract_sweep && hasPermission(Pkey.TabContractSweep.ViewDetail)),
    approval: hasPermission(Pkey.Approve.prefix) || hasPermission(Pkey.Reject),
    manualVerification:
      ((isTab.portal || isTab.all) &&
        hasPermission(Pkey.TabPortalWithdraw.prefix)) ||
      ((isTab.portal || isTab.all) &&
        hasPermission(Pkey.ManualVerify.prefix)) ||
      ((isTab.portal || isTab.all) && hasPermission(Pkey.VerifyFailed)),
    download:
      (isTab.all && hasPermission(Pkey.TabAll.Download)) ||
      (isTab.api && hasPermission(Pkey.TabApiWithdraw.Download)) ||
      (isTab.portal && hasPermission(Pkey.TabPortalWithdraw.Download)) ||
      (isTab.sweep && hasPermission(Pkey.TabSweep.Download)) ||
      (isTab.swap && hasPermission(Pkey.TabSwap.Download)) ||
      (isTab.contract_sweep && hasPermission(Pkey.TabContractSweep.Download)),
    sign:
      (isTab.all && hasPermission(Pkey.TabAll.Sign)) ||
      (isTab.api && hasPermission(Pkey.TabApiWithdraw.Sign)) ||
      (isTab.portal && hasPermission(Pkey.TabPortalWithdraw.Sign)) ||
      (isTab.sweep && hasPermission(Pkey.TabSweep.Sign)) ||
      (isTab.swap && hasPermission(Pkey.TabSwap.Sign)) ||
      (isTab.contract_sweep && hasPermission(Pkey.TabContractSweep.Sign)),
  };

  const columns: GridColDef[] = [
    useGenGridCol("operation", t("operation"), {
      minWidth: 200,
      renderCell: (params) => cellButtons(params, canShow),
    }),
    useGenGridCol("batch_id", t("batch_id")),
    useGenGridCol("approval_progress", t("approval_progress"), {
      renderCell: HistoryButton,
      minWidth: 120,
    }),
    useGenGridCol("creator_type", t("creator_type"), { minWidth: 120 }),
    useGenGridCol("operation_type", t("operation_type"), { minWidth: 120 }),
    useGenGridCol("status", t("status"), { minWidth: 120 }),
    useGenGridCol("multisig", t("multisig"), {
      minWidth: 120,
      renderCell: (params) => (
        <MultisigCell currentTab={tabName} rowData={params.row} />
      ),
    }),
    useGenGridCol("chain_name", t("chain_name"), { minWidth: 120 }),
    useGenGridCol("asset_name", t("asset_name")),
    useGenGridCol("smart_account_type", t("smart_account_type")),
    useGenGridCol("max_slippage", t("max_slippage")),
    useGenGridCol("swap_execution_type", t("swap_execution_type"), {
      minWidth: 120,
    }),
    useGenGridCol("currency_from", t("currency_from")),
    useGenGridCol("total_amount_from", t("total_amount_from"), {
      minWidth: 170,
    }),
    // useGenGridCol("currency_to", t("currency_to")),
    // useGenGridCol("total_amount_to", t("total_amount_to"), { minWidth: 170 }),
    useGenGridCol("total_amount", t("totalAmount"), { minWidth: 170 }),
    useGenGridCol("wallet_major_version", t("wallet_major_version"), {
      minWidth: 150,
    }),
    useGenGridCol("total_operation", t("total_operation")),
    useGenGridCol("created_by", t("created_by")),
    useGenGridCol("created_date", t("created_date")),
    useGenGridCol("last_executed_by", t("last_executed_by"), { minWidth: 120 }),
    useGenGridCol("last_executed_date", t("last_executed_date"), {
      minWidth: 170,
    }),
    useGenGridCol("last_modified_date", t("last_modified_date")),
    useGenGridCol("uploaded_by", t("uploaded_signer"), { minWidth: 120 }),
    useGenGridCol("miner_fee_asset_name", t("miner_fee_asset_name"), {
      minWidth: 130,
    }),
    useGenGridCol("estimated_miner_fee", t("estimated_miner_fee"), {
      minWidth: 170,
      renderCell: ListEstimatedCell,
    }),
    useGenGridCol("prepared_miner_fee", t("prepared_miner_fee"), {
      minWidth: 170,
    }),
    useGenGridCol("remarks", t("remark"), { minWidth: 170 }),
  ];

  const { filteredColumns } = getFilteredColumns(columns, [
    {
      fieldName: "operation",
      shouldFilter: Object.values(canShow).every((val) => !val),
    },
    ...SWAP_TAB_OMIT_KEYS.map((key) => ({
      fieldName: key,
      shouldFilter: isTab.swap,
    })),
    ...NON_SWAP_TAB_OMIT_KEYS.map((key) => ({
      fieldName: key,
      shouldFilter: !isTab.swap,
    })),
  ]);

  const { colsShown, setColsShown, hasColHidden, localeText } = useColumnHide(
    filteredColumns,
    getColumnHideConfig(),
  );

  return (
    <Container maxWidth={false} disableGutters>
      <ColumnHiddenMessage hasColHidden={hasColHidden} />
      <DataGrid
        {...dataGridDefaults}
        localeText={localeText}
        columnVisibilityModel={colsShown}
        onColumnVisibilityModelChange={setColsShown}
        rows={content}
        rowCount={list.count}
        columns={filteredColumns}
        page={page}
        onPageChange={setPage}
        components={{
          NoRowsOverlay,
          Footer: CustomPagination,
        }}
        componentsProps={{
          footer: { totalRecords },
        }}
      />
    </Container>
  );
}

function ActionComponent(props: any) {
  const { params, setUploadDialog } = props;
  const canShow = props.canShow as {
    viewDetail: boolean;
    approval: boolean;
    download: boolean;
    sign: boolean;
    manualVerification: boolean;
  };
  const tabName = props.tabName as IoperationTab;
  const tabApi = getTabApis(tabName);
  const translate = useTranslate();
  const { alerting } = useAlerting();

  const t = (key: string, option?: string) =>
    translate(`operations.${key}`, option);
  const te = (key: string, option?: string) =>
    translate(`enumConstants.${key}`, option);
  const zusDialog = useZusDialogStore();
  const { hasPermission } = usePermission();

  const moreOperationDetailsApi = async () => {
    const batchId = params.row?.batch_id;
    if (!batchId) alerting("error", `batchId has error, ${batchId}`);
    zusDialog.open("recordDialog", {
      batchId,
      page: 0,
      pageSize: tableConfig.pageSize,
      chain_type: params.row?.chain_type,
      chain_id: params.row?.chain_id,
      creator_type: params.row?.rawData?.creator_type,
    });
  };

  const onDownloadTransaction = async () => {
    zusDialog.openExtra("loadingDialog");
    const onDownloadResponse = await tabApi.download(params.row.batch_id);
    zusDialog.closeExtra();

    if (!onDownloadResponse) {
      return;
    }
    const element = document.createElement("a");
    const file = new Blob([JSON.stringify(onDownloadResponse)], {
      type: "text/plain",
    });
    const fileParam = params.row;
    const chain_code = fileParam.rawData?.chain_code || "";
    const timeStamp = todaysDateForFileName();
    element.href = URL.createObjectURL(file);
    element.download = `${fileParam.batch_id}_${chain_code}_${fileParam.asset_name}_${timeStamp}_unsigned_tx.txt`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
    return onDownloadResponse;
  };

  const onBroadcast = () => {
    setUploadDialog({ bol: true, info: params.row });
  };

  const mergeArrays = (
    opHourlyStatArr: Array<opDayStat>,
    opDayStatArr: Array<opDayStat>,
  ) => {
    const material = {
      chain_type: params.row.chain_type,
      chain_id: params.row.chain_id,
      asset_name: params.row.asset_name,
    };
    const decimal = getDecimal(material);
    const toAmount = (amount: number) => displayAmount(amount, decimal);
    // Concatenate the two arrays
    const hourlyStatRes = opHourlyStatArr.map((item: any) => {
      const newObj = { ...item };
      newObj["hrly_amt"] = toAmount(newObj["hourly_amount"]);
      newObj["hrly_count"] = newObj["hourly_count"];
      delete newObj["hourly_amount"];
      delete newObj["hourly_count"];
      return newObj;
    });
    const dayStatRes = opDayStatArr.map((item: any) => {
      const newObj = { ...item };
      newObj["day_hourly_amount"] = toAmount(newObj["hourly_amount"]);
      return newObj;
    });
    const mergedArr = hourlyStatRes.concat(dayStatRes);

    // Create a map to store the merged objects
    const mergedMap = new Map();
    mergedArr.forEach((obj: any) => {
      // Get the address property
      const address = obj.address;
      const operation_seq = obj.operation_seq;
      // Check if this address is already in the map
      if (mergedMap.has(address)) {
        // If it is, merge the current object with the existing one
        const existingObj = mergedMap.get(address);
        const mergedObj = {
          address: address,
          operation_seq,
          hrly_amt: obj.hrly_amt || existingObj.hrly_amt,
          day_hourly_amount:
            obj.day_hourly_amount || existingObj.day_hourly_amount,
          hrly_count: obj.hrly_count || existingObj.hrly_count,
          hourly_count: obj.hourly_count || existingObj.hourly_count,
          asset_name: obj.asset_name || existingObj.asset_name,
        };
        mergedMap.set(address, mergedObj);
      } else {
        // If it's not, add the current object to the map
        mergedMap.set(address, obj);
      }
    });
    // Convert the map back to an array and return it
    const mergedArrWithNoDuplicates = Array.from(mergedMap.values());
    const addIdToMergedArr = mergedArrWithNoDuplicates.map(
      (item, index: number) => {
        return { ...item, id: index + 1 };
      },
    );

    return addIdToMergedArr;
  };

  const onPreApprove = async () => {
    const getDatailsData = async () => {
      if (params.row.rawData.operation_type == enumOperationType.Uniswap) {
        const uniswapDetailsRes = await tabApi.opDetail({
          batchId: params.row.batch_id,
          page: 0,
          pageSize: tableConfig.pageSize,
          chain_type: params.row?.chain_type,
          chain_id: params.row?.chain_id,
          creator_type: params.row?.creator_type,
        });

        if (!uniswapDetailsRes) {
          return { uniswapDetailsArr: [] };
        }

        let totalPayAmountBigNum = new BigNumber(0);
        let payCurrency = "";
        let payDecimals = 0;
        let totalGetAmountBigNum = new BigNumber(0);
        let getCurrency = "";
        let getDecimals = 0;

        const displayAmountWithCurrency = (
          amount: string | number,
          decimals: number,
          currency: string,
        ) => {
          return displayAmount(amount, decimals) + " " + currency;
        };

        const transformedUniswapDetailsRes = uniswapDetailsRes.rows.map(
          ({
            id,
            operation_seq,
            swap_operation,
            amount,
            asset_name: pay_currency,
            decimals,
            to_address: wallet_address,
          }) => {
            totalPayAmountBigNum = totalPayAmountBigNum.plus(
              new BigNumber(amount),
            );
            payCurrency = pay_currency;
            payDecimals = decimals;
            const pay_total_amount = displayAmountWithCurrency(
              amount,
              decimals,
              pay_currency,
            );

            const {
              amount_get,
              decimals_get,
              asset_name_get: get_currency,
              execution_type,
            } = swap_operation;

            totalGetAmountBigNum = totalGetAmountBigNum.plus(
              new BigNumber(amount_get),
            );
            getCurrency = get_currency;
            getDecimals = decimals_get;
            const get_total_amount = displayAmountWithCurrency(
              amount_get,
              decimals_get,
              get_currency,
            );

            return {
              id,
              operation_seq,
              swap_execution_type: te(SwapExecutionType[execution_type]),
              wallet_address,
              pay_currency,
              pay_total_amount,
              get_total_amount,
              get_currency,
            };
          },
        );

        const totalPayAmount = displayAmountWithCurrency(
          totalPayAmountBigNum.toNumber(),
          payDecimals,
          payCurrency,
        );
        const totalGetAmount = displayAmountWithCurrency(
          totalGetAmountBigNum.toNumber(),
          getDecimals,
          getCurrency,
        );

        return {
          uniswapDetailsArr: [
            ...transformedUniswapDetailsRes,
            // ...transformedUniswapDetailsRes.map(
            //   ({ id, pay_total_amount, ...rest }) => ({
            //     id: id + 1,
            //     pay_total_amount: "1.001 USDC",
            //     ...rest,
            //   }),
            // ),
            // ...transformedUniswapDetailsRes.map(
            //   ({ id, pay_total_amount, ...rest }) => ({
            //     id: id + 2,
            //     pay_total_amount: "1,000.001 USDC",
            //     ...rest,
            //   }),
            // ),
          ],
          totalGetAmount,
          totalPayAmount,
          addressCount: transformedUniswapDetailsRes.length,
        };
      } else {
        const rawOpDetails = await tabApi.stat({
          batch_id: params.row.batch_id,
        });

        const reason: any = await tabApi.opReason({
          batch_id: params.row.batch_id,
        });
        const opDetailList = mergeArrays(rawOpDetails.hour, rawOpDetails.day);
        const amtWithoutCommas = opDetailList.map((item: any) => {
          const newObj = { ...item };
          if (newObj.day_hourly_amount) {
            newObj.day_hourly_amount = newObj.day_hourly_amount.replaceAll(
              ",",
              "",
            );
          }
          if (newObj.hrly_amt) {
            newObj.hrly_amt = newObj.hrly_amt.replaceAll(",", "");
          }
          return newObj;
        });
        const trxHrlyCount = calculateSum(amtWithoutCommas, "hrly_count");
        const trxHrlyAmt = calculateSum(amtWithoutCommas, "hrly_amt");
        const trxDailyAmt = calculateSum(amtWithoutCommas, "day_hourly_amount");
        const { checking_hour } = rawOpDetails;

        const reasonWithCheckingTime = reason.map((item: any) => {
          return { ...item, checking_hour: checking_hour };
        });

        return {
          opArrDetail: opDetailList,
          trxDayAmt: trxDailyAmt ? trxDailyAmt : "",
          trxHrCount: trxHrlyCount ? trxHrlyCount : "",
          trxHrAmt: trxHrlyAmt ? trxHrlyAmt : "",
          approveReason: reasonWithCheckingTime, //each element in an array with checking_hour
          checkTime: checking_hour,
        };
      }
    };

    const detailsData = await getDatailsData();

    const fullParams = {
      ...params.row,
      ...detailsData,
    };

    zusDialog.open("approvalDialog", {
      from: "operation",
      row: fullParams,
      tabName,
    });
  };

  const onPreManualVerify = async () => {
    const rawOpDetails = await tabApi.stat({
      batch_id: params.row.batch_id,
    });

    const opDetailList = mergeArrays(rawOpDetails.hour, rawOpDetails.day);
    const amtWithoutCommas = opDetailList.map((item: any) => {
      const newObj = { ...item };
      if (newObj.day_hourly_amount) {
        newObj.day_hourly_amount = newObj.day_hourly_amount.replaceAll(",", "");
      }
      if (newObj.hrly_amt) {
        newObj.hrly_amt = newObj.hrly_amt.replaceAll(",", "");
      }
      return newObj;
    });
    const trxHrlyCount = calculateSum(amtWithoutCommas, "hrly_count");
    const trxHrlyAmt = calculateSum(amtWithoutCommas, "hrly_amt");
    const trxDailyAmt = calculateSum(amtWithoutCommas, "day_hourly_amount");
    const { checking_hour } = rawOpDetails;
    const fullParams = {
      ...params.row,
      opArrDetail: opDetailList,
      trxDayAmt: trxDailyAmt ? trxDailyAmt : "",
      trxHrCount: trxHrlyCount ? trxHrlyCount : "",
      trxHrAmt: trxHrlyAmt ? trxHrlyAmt : "",
      checkTime: checking_hour,
    };

    zusDialog.open("manualVerificationDialog", {
      from: "operation",
      row: fullParams,
      tabName,
    });
  };

  const rowStatus = String(params.row.rawData?.status || NaN);
  const isStatus = {
    prepared: rowStatus === enumNewMerchantOperationStatus.Prepared,
    approve:
      rowStatus === enumNewMerchantOperationStatus.Verified ||
      rowStatus === enumNewMerchantOperationStatus.Multisig,
    manualVerification:
      rowStatus === enumNewMerchantOperationStatus.ManualVerification,
  };

  return (
    <Box
      sx={{ display: "flex", justifyContent: "space-evenly", width: "100%" }}
    >
      <OpIconButton
        title={t("view")}
        url={OpView}
        onClick={moreOperationDetailsApi}
        isHidden={!canShow.viewDetail}
      />
      {/* TODO: dynamic render the approve button */}
      <OpIconButton
        title={t("approveAndRejectButton")}
        url={OpApprove}
        isHidden={!canShow.approval || isStatus.manualVerification}
        isDisabled={!isStatus.approve}
        onClick={onPreApprove}
      />
      <OpIconButton
        title={t("manualVerificationButton")}
        url={OpApprove}
        isHidden={!canShow.manualVerification || !isStatus.manualVerification}
        isDisabled={!isStatus.manualVerification}
        onClick={onPreManualVerify}
      />
      <OpIconButton
        title={t("download")}
        url={OpDownload}
        isHidden={!canShow.download}
        isDisabled={!isStatus.prepared}
        onClick={onDownloadTransaction}
      />
      <OpIconButton
        title={t("sign")}
        url={OpSign}
        isHidden={!canShow.sign}
        isDisabled={!isStatus.prepared}
        onClick={onBroadcast}
      />
    </Box>
  );
}

export function ReEstimateDialog(props: { tabName: IoperationTab }) {
  const zusDialog = useZusDialogStore();
  const matchedDialog = zusDialog.match("operationDialog");
  const translate = useTranslate();
  const t = (key: string) => translate(`${translatePrefix}.${key}`);
  const { tabName } = props;
  const {
    batch_id,
    status,
    miner_fee_asset_name,
    estimated_miner_fee,
    last_estimated_miner_fee_date,
  } = zusDialog.meta;
  const detail = {
    id: -1,
    batch_id,
    miner_fee_asset_name,
    estimated_miner_fee,
    last_estimated_miner_fee_date,
  };
  const ActionCell = (item: GridRenderCellParams) => {
    const { batch_id } = item.row;
    const onEstimate = async () => {
      const tabApi = getTabApis(tabName);
      const res = await tabApi.estimate({ batchId: batch_id });
      if (!res) {
        return;
      }
      const estimated_miner_fee = t("estimating");
      zusDialog.setMeta({ ...zusDialog.meta, estimated_miner_fee });
    };
    const isButtonShow = [
      Enum.newMerchantOperationStatus.Verified,
      Enum.newMerchantOperationStatus.Approved,
      Enum.newMerchantOperationStatus.Prepared,
      Enum.newMerchantOperationStatus.Signed,
      Enum.newMerchantOperationStatus.SignFailed,
      Enum.newMerchantOperationStatus.Rejected,
    ].some((s) => s === String(status));
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-evenly",
          width: "55%",
        }}
      >
        <ToolCryptoText tooltip={estimated_miner_fee} />
        <OpIconButton
          title={t("re_estimate")}
          url={OpEstimate}
          onClick={onEstimate}
          isHidden={!isButtonShow}
          isDisabled={
            String(status) !== Enum.newMerchantOperationStatus.Verified
          }
          size="1rem"
        />
      </Box>
    );
  };
  const columns: GridColDef[] = [
    useGenGridCol("batch_id", t("batch_id")),
    useGenGridCol("miner_fee_asset_name", t("miner_fee_asset_name")),
    useGenGridCol("estimated_miner_fee", t("estimated_miner_fee"), {
      renderCell: ActionCell,
    }),
    useGenGridCol(
      "last_estimated_miner_fee_date",
      t("last_estimated_miner_fee_date"),
    ),
  ];
  if (!matchedDialog) return null;
  return (
    <DialogInOne
      {...{
        title: t("estimated_miner_fee"),
        self: {
          open: matchedDialog,
          onClose: zusDialog.close,
        },
        content: (
          <DataGrid
            {...dataGridDefaults}
            disableColumnMenu
            paginationMode="client"
            hideFooterPagination
            rows={[detail]}
            columns={columns}
            onPageChange={() => {}}
            rowsPerPageOptions={tableConfig.rowsPerPageOptions}
            components={{
              NoRowsOverlay,
            }}
          />
        ),
        onCancel: () => zusDialog.close(),
        isConfirmHidden: true,
        onConfirm: () => {},
        rest: { maxWidth: "md" },
      }}
    />
  );
}
